import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Button from "@mui/material/Button";
import { FilterMatchMode } from "primereact/api";
import { Column, ColumnFilterElementTemplateOptions } from "primereact/column";
import {
  DataTable,
  DataTableMultiSortMetaType,
  DataTableSortOrderType,
  DataTableSortParams,
} from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import React, { useContext, useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { TextTemplate } from "../../components/shared/Grid/BodyTemplates";
import { GlobalFilter } from "../../components/shared/Grid/GlobalFilterTemplate";
import { usePage } from "../../routes";
import { AccessControlContext } from "../contexts/AccessControlContext";
import DataTableSkeleton from "../shared/DataTableSkeleton";
import RefreshLoadingButton from "../shared/RefreshLoadingButton";
import { LOCALSTORAGE_KEYS } from "../shared/constants";
import { getAssetTypeLabel } from "../shared/constants/managedSurveyAssetTypes";
import { WorkflowV1 } from "../shared/interfaces/WorkflowV1";
import { WorkflowV2 } from "../shared/interfaces/WorkflowV2";
import type { WorkflowType } from "./workflowReducer";
import { WorkflowTypeChip } from "./WorkflowTypeChip";
import { WorkflowViewer } from "./WorkflowViewer";

interface Props {
  workflowData?: WorkflowV1[];
  workflowDataLoading: boolean;
  workflowDataFetching: boolean;
  onDataRefreshClick: () => void;
  onNewWorkflowClick: () => void;
  customerId?: number;
}

const sortFieldStorageKey = LOCALSTORAGE_KEYS.workflowsSortField;
const sortOrderStorageKey = LOCALSTORAGE_KEYS.workflowsSortOrder;

export function WorkflowGrid(props: Props) {
  const { t } = useTranslation();
  const workflowViewerPath = usePage(WorkflowViewer.name)?.path;

  const [globalFilter, setGlobalFilter] = useState<string>("");

  const accessControlContext = useContext(AccessControlContext);
  const userCanCreateWorkflow = accessControlContext.canI("add", "workflow", props.customerId);

  function handleGlobalFilterInput(event: React.FormEvent<HTMLInputElement>): void {
    const element = event.target as HTMLInputElement;

    setGlobalFilter(element.value);
  }

  const [multiSortMeta, setMultiSortMeta] = useState<DataTableMultiSortMetaType>();

  const onSort = (e: DataTableSortParams) => {
    const field = e.multiSortMeta?.at(0)?.field as string;
    const order = e.multiSortMeta?.at(0)?.order;
    localStorage.setItem(sortFieldStorageKey, field);
    order && localStorage.setItem(sortOrderStorageKey, order?.toString());
    const sort: DataTableMultiSortMetaType = [{ field: field, order: order }];
    setMultiSortMeta(sort);
  };

  useEffect(() => {
    const field = localStorage.getItem(sortFieldStorageKey) as string;
    const order = localStorage.getItem(sortOrderStorageKey) as DataTableSortOrderType;
    const sort: DataTableMultiSortMetaType = [{ field: field, order: order }];
    setMultiSortMeta(sort);
  }, [props]);

  const headerToolbar = (
    <Row>
      <Col>
        {userCanCreateWorkflow && (
          <Button
            variant="contained"
            color="primary"
            onClick={props.onNewWorkflowClick}
            startIcon={<FontAwesomeIcon style={{ cursor: "pointer" }} icon={faPlus} />}
          >
            {t("workflows.newWorkflow")}
          </Button>
        )}
      </Col>
      <Col className="d-flex justify-content-center">
        <GlobalFilter onInput={handleGlobalFilterInput} />
      </Col>
      <Col className="d-flex justify-content-end">
        <RefreshLoadingButton isLoading={props.workflowDataFetching} onClick={props.onDataRefreshClick} />
      </Col>
    </Row>
  );

  const actionBodyTemplate = (rowData: WorkflowV1) => {
    return (
      <Button
        variant="contained"
        color="primary"
        component={Link}
        to={{
          pathname: workflowViewerPath?.replace(":workflowId", rowData.id.toString()),
        }}
      >
        <VisibilityIcon />
      </Button>
    );
  };

  const workflowTypeItemTemplate = (workflowType: WorkflowType) => {
    return workflowType ? (
      <WorkflowTypeChip workflowType={workflowType} />
    ) : (
      t("common.filter", { val: "$t(workflows.workflowType)" })
    );
  };

  const filterWorkflowType = (value: string, filter: WorkflowType) => {
    if (filter === "generic") {
      return !value;
    } else if (filter === "substation") {
      return Boolean(value);
    } else return true;
  };

  const workflowTypeFilter = (options: ColumnFilterElementTemplateOptions) => (
    <Dropdown
      value={options.value}
      options={["substation", "generic"] as WorkflowType[]}
      onChange={(e) => options.filterApplyCallback(e.value)}
      showClear
      itemTemplate={workflowTypeItemTemplate}
      valueTemplate={workflowTypeItemTemplate}
    />
  );

  const getAssetNames = (rowData: WorkflowV1 | WorkflowV2) => {
    if (rowData.workflowDetail.version === 1) {
      return <TextTemplate rowData={rowData} field={"switchGearName"} header={t("switchgear.switchgear")} />;
    } else if (rowData.workflowDetail.version === 2) {
      const text = rowData.workflowDetail.assets
        .filter((asset) => asset.name && asset.asset_type)
        .map((asset) => `${asset.name} (${t(`assetTypes.${getAssetTypeLabel(asset.asset_type)}`)})`)
        .join(", ");

      return <>{text}</>;
    }
  };

  if (props.workflowDataLoading) return <DataTableSkeleton />;
  else
    return (
      <React.Fragment>
        <div className="datatable-responsive">
          <DataTable
            value={props.workflowData}
            header={headerToolbar}
            // If the global filter is an empty string add a space, otherwise some results may get filtered out
            globalFilter={globalFilter === "" ? " " : globalFilter}
            filterDisplay="row"
            removableSort
            sortMode="multiple"
            className="p-datatable-responsive p-datatable-gridlines p-datatable-sm"
            emptyMessage={t("common.emptyMessage", {
              val: "$t(workflows.workflows )",
            })}
            loading={props.workflowDataLoading}
            multiSortMeta={multiSortMeta}
            onSort={onSort}
          >
            <Column
              field={"id"}
              header={t("workflows.workflowId")}
              body={(rowData: WorkflowV1) => (
                <TextTemplate rowData={rowData} field={"id"} header={t("workflows.workflowId")} />
              )}
              filter
              showFilterMenu={false}
              showClearButton={false}
              sortable
              sortField="id"
            />
            <Column
              field={"description"}
              header={t("common.summary")}
              body={(rowData: WorkflowV1) => (
                <TextTemplate rowData={rowData} field={"description"} header={t("common.summary")} />
              )}
              filter
              sortable
              filterMatchMode={FilterMatchMode.CONTAINS}
              showFilterMenu={false}
              showClearButton={false}
              sortField="description"
            />
            <Column
              field={"workflowDetail.substation_id"}
              header={t("workflows.workflowType")}
              body={(rowData: WorkflowV1) =>
                workflowTypeItemTemplate(rowData.workflowDetail.substation_id ? "substation" : "generic")
              }
              filter
              sortable
              filterElement={workflowTypeFilter}
              filterMatchMode={FilterMatchMode.CUSTOM}
              filterFunction={filterWorkflowType}
              showFilterMenu={false}
              showClearButton={false}
              sortField="workflowDetail.substation_id"
            />
            <Column
              field={"substationName"}
              header={t("substations.substation")}
              body={(rowData: WorkflowV1) => (
                <TextTemplate rowData={rowData} field={"substationName"} header={t("substations.substation")} />
              )}
              filter
              sortable
              filterMatchMode={FilterMatchMode.CONTAINS}
              showFilterMenu={false}
              showClearButton={false}
              sortField="substationName"
            />
            <Column
              field={"switchGearName"}
              header={t("asset.assets")}
              body={(rowData: WorkflowV1) => getAssetNames(rowData)}
              filter
              filterMatchMode={FilterMatchMode.CONTAINS}
              showFilterMenu={false}
              showClearButton={false}
              sortField="switchGearName"
            />
            <Column
              body={actionBodyTemplate}
              headerStyle={{ width: "8em", textAlign: "center" }}
              bodyStyle={{ textAlign: "center", overflow: "visible" }}
              header={t("common.actions")}
            />
          </DataTable>
        </div>
      </React.Fragment>
    );
}
